/**
 * ID Generator Utility for UI-Model-1
 *
 * Generates unique IDs for containers, items, and other entities.
 */

/**
 * Generates a unique instance ID for a component
 * Format: {baseId}-instance-{uuid}
 */
export const generateInstanceId = (baseId: string): string => {
  const uuid = crypto.randomUUID ? crypto.randomUUID() : generateUUID();
  return `${baseId}-instance-${uuid}`;
};

/**
 * Generates a unique container ID
 * Format: container-{uuid}
 */
export const generateContainerId = (): string => {
  const uuid = crypto.randomUUID ? crypto.randomUUID() : generateUUID();
  return `container-${uuid}`;
};

/**
 * Generates a unique group ID
 * Format: group-{uuid}
 */
export const generateGroupId = (): string => {
  const uuid = crypto.randomUUID ? crypto.randomUUID() : generateUUID();
  return `group-${uuid}`;
};

/**
 * Generates a unique page ID
 * Format: page-{uuid}
 */
export const generatePageId = (): string => {
  const uuid = crypto.randomUUID ? crypto.randomUUID() : generateUUID();
  return `page-${uuid}`;
};

/**
 * Generates a canvas ID from group and page IDs
 * Format: {pageId}
 */
export const generateCanvasId = (pageId: string): string => {
  return pageId;
};

/**
 * Fallback UUID generator for environments without crypto.randomUUID
 */
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Extracts the base ID from an instance ID
 * Returns the original ID if not an instance ID
 */
export const getBaseId = (instanceId: string): string => {
  const instanceMatch = instanceId.match(/^(.+)-instance-[a-f0-9-]+$/);
  return instanceMatch ? instanceMatch[1] : instanceId;
};

/**
 * Checks if an ID is an instance ID
 */
export const isInstanceId = (id: string): boolean => {
  return /-instance-[a-f0-9-]+$/.test(id);
};

/**
 * Generates a unique ID with a prefix
 */
export const generateId = (prefix: string): string => {
  const uuid = crypto.randomUUID ? crypto.randomUUID() : generateUUID();
  return `${prefix}-${uuid}`;
};
